/* eslint-disable max-statements, no-underscore-dangle */
/**
 * Brand asset resolution for frontend-login (ZET-4554 CDN POC / ZET-7206 Origin Brand Config).
 *
 * Logo priority:
 * 1. window.__BRAND.orgs[orgId].logoUrl (Secondary Scope multi-tenant)
 * 2. window.__BRAND.logoUrl (v1 Scope dedicated host)
 * 3. /brands/{orgId}/logo.svg (Same-origin convention)
 * 4. REACT_APP_BRAND_LOGO_URL — absolute override
 * 5. {REACT_APP_BRAND_CDN_BASE}/{orgId}/logo.svg
 * 6. Bundled Zinier wordmark
 *
 * Favicon priority:
 * 1. window.__BRAND.orgs[orgId].faviconUrl
 * 2. window.__BRAND.faviconUrl
 * 3. /brands/{orgId}/favicon.svg
 * 4. REACT_APP_BRAND_FAVICON_URL
 * 5. {REACT_APP_BRAND_CDN_BASE}/{orgId}/favicon.svg (probed; else default /favicon.ico)
 */
import defaultLogo from '../features/Logo/zinier-logo-with-name-black.svg';
import {
  resolveActiveOrgId,
  applyBrandDocumentTitle,
  DEFAULT_DOCUMENT_TITLE
} from '../languages';

export const CDN_LOGO_FILE = 'logo.svg';
export const CDN_FAVICON_FILE = 'favicon.svg';

export { resolveActiveOrgId, applyBrandDocumentTitle, DEFAULT_DOCUMENT_TITLE };
export const getOrgFromUrl = resolveActiveOrgId;

export const getDefaultBrandLogo = () => defaultLogo;

export const getBrandCdnBase = () => {
  const base = process.env.REACT_APP_BRAND_CDN_BASE;
  if (!base) {
    return null;
  }
  return String(base).replace(/\/$/, '');
};

/**
 * Build a CDN asset URL for an org.
 * @param {string} orgId
 * @param {string} assetFile e.g. logo.svg
 * @returns {string|null}
 */
export const buildCdnAssetUrl = (orgId, assetFile) => {
  const base = getBrandCdnBase();
  const trimmedOrg = resolveActiveOrgId(orgId);
  if (!base || !trimmedOrg || !assetFile) {
    return null;
  }
  return `${base}/${encodeURIComponent(trimmedOrg)}/${assetFile}`;
};

/**
 * Access the origin brand configuration injected via /brand.js.
 * @returns {object|null}
 */
export const getOriginBrandConfig = () => {
  if (typeof window !== 'undefined' && window.__BRAND && typeof window.__BRAND === 'object') {
    return window.__BRAND;
  }
  return null;
};

/**
 * Build a same-origin asset URL under the origin /brands/ folder (Secondary Scope).
 * @param {string} orgId
 * @param {string} assetFile e.g. logo.svg
 * @returns {string|null}
 */
export const buildSameOriginAssetUrl = (orgId, assetFile) => {
  const trimmedOrg = resolveActiveOrgId(orgId);
  if (!trimmedOrg || !assetFile) {
    return null;
  }
  const brand = getOriginBrandConfig();
  const base = typeof brand?.brandsBase === 'string'
    ? brand.brandsBase.replace(/\/$/, '')
    : `${process.env.PUBLIC_URL || ''}/brands`;
  return `${base}/${encodeURIComponent(trimmedOrg)}/${assetFile}`;
};

const resolveOriginAsset = (orgId, assetKey, aliasKey, defaultAssetFile) => {
  const brand = getOriginBrandConfig();
  if (!brand) {
    return null;
  }
  const trimmedOrg = resolveActiveOrgId(orgId);
  const orgConfig = trimmedOrg && brand.orgs && brand.orgs[trimmedOrg];
  if (orgConfig) {
    if (typeof orgConfig === 'string' && assetKey === 'logoUrl') {
      return orgConfig;
    }
    const resolved = orgConfig[assetKey] || (aliasKey && orgConfig[aliasKey]);
    if (resolved) {
      return resolved;
    }
  }
  const topAsset = brand[assetKey] || (aliasKey && brand[aliasKey]);
  if (topAsset) {
    return topAsset;
  }
  if (trimmedOrg && (brand.brandsBase || brand.sameOriginBrands)) {
    return buildSameOriginAssetUrl(trimmedOrg, defaultAssetFile);
  }
  return null;
};

/**
 * Resolve logo URL from window.__BRAND (supporting both v1 and Secondary Scope).
 * @param {string} [orgId]
 * @returns {string|null}
 */
export const resolveOriginBrandLogo = (orgId) => {
  return resolveOriginAsset(orgId, 'logoUrl', 'logo', CDN_LOGO_FILE);
};

/**
 * Resolve favicon URL from window.__BRAND (supporting both v1 and Secondary Scope).
 * @param {string} [orgId]
 * @returns {string|null}
 */
export const resolveOriginBrandFavicon = (orgId) => {
  return resolveOriginAsset(orgId, 'faviconUrl', 'favicon', CDN_FAVICON_FILE);
};

const resolveConfiguredLogoUrl = (orgId) => {
  const originLogo = resolveOriginBrandLogo(orgId);
  if (originLogo) {
    return originLogo;
  }
  const envLogo = process.env.REACT_APP_BRAND_LOGO_URL;
  if (envLogo) {
    return envLogo;
  }
  return buildCdnAssetUrl(orgId, CDN_LOGO_FILE);
};

/**
 * Resolve the logo URL/src for login screens.
 * @param {string} [orgId]
 * @returns {string}
 */
export const getBrandLogoSrc = (orgId) => {
  return resolveConfiguredLogoUrl(orgId) || defaultLogo;
};

/**
 * Resolve favicon candidate URL (may still 404 — callers should probe).
 * @param {string} [orgId]
 * @returns {string|null}
 */
export const getBrandFaviconSrc = (orgId) => {
  const originFavicon = resolveOriginBrandFavicon(orgId);
  if (originFavicon) {
    return originFavicon;
  }
  const envFavicon = process.env.REACT_APP_BRAND_FAVICON_URL;
  if (envFavicon) {
    return envFavicon;
  }
  return buildCdnAssetUrl(orgId, CDN_FAVICON_FILE) ||
    resolveConfiguredLogoUrl(orgId);
};

export const DEFAULT_FAVICON_HREF = `${process.env.PUBLIC_URL || ''}/favicon.ico`;

let faviconRequestId = 0;

export const resetFaviconRequestId = () => {
  faviconRequestId = 0;
};

const nextFaviconRequestId = () => {
  faviconRequestId = faviconRequestId + 1;
  return faviconRequestId;
};

const isLatestFaviconRequest = (requestId) => requestId === faviconRequestId;

const setDocumentFavicon = (href) => {
  if (typeof document === 'undefined' || !href) {
    return;
  }
  const brand = getOriginBrandConfig();
  const versionParam = brand?.version ? `v=${brand.version}` : `v=${Date.now()}`;
  const sep = href.includes('?') ? '&' : '?';
  const finalHref = href.includes('v=') ? href : `${href}${sep}${versionParam}`;

  const existing = document.querySelectorAll('link[rel*=\'icon\']');
  existing.forEach((node) => {
    if (node.parentNode) {
      node.parentNode.removeChild(node);
    }
  });

  const mimeType = finalHref.includes('.png')
    ? 'image/png'
    : (finalHref.includes('.svg') ? 'image/svg+xml' : 'image/x-icon');

  const shortcutLink = document.createElement('link');
  shortcutLink.rel = 'shortcut icon';
  shortcutLink.type = mimeType;
  shortcutLink.href = finalHref;
  document.head.appendChild(shortcutLink);

  const iconLink = document.createElement('link');
  iconLink.rel = 'icon';
  iconLink.type = mimeType;
  iconLink.href = finalHref;
  document.head.appendChild(iconLink);
};

const probeImageUrl = (url) => new Promise((resolve) => {
  if (!url || typeof Image === 'undefined') {
    resolve(false);
    return;
  }
  const img = new Image();
  img.onload = () => resolve(true);
  img.onerror = () => resolve(false);
  img.src = url;
});

const resolveFaviconHref = async (orgId) => {
  const candidate = getBrandFaviconSrc(orgId);
  if (!candidate || candidate === defaultLogo) {
    return DEFAULT_FAVICON_HREF;
  }
  const originFavicon = resolveOriginBrandFavicon(orgId);
  if (originFavicon && originFavicon === candidate) {
    return candidate;
  }
  const loaded = await probeImageUrl(candidate);
  return loaded ? candidate : DEFAULT_FAVICON_HREF;
};

/**
 * Force default Zinier favicon and cancel in-flight brand probes.
 */
export const resetBrandFavicon = () => {
  if (typeof document === 'undefined') {
    return;
  }
  nextFaviconRequestId();
  setDocumentFavicon(DEFAULT_FAVICON_HREF);
};

/**
 * Apply brand favicon when the asset loads; otherwise restore default.
 * @param {string} [orgId]
 * @returns {Promise<void>}
 */
export const applyBrandFavicon = async (orgId) => {
  if (typeof document === 'undefined') {
    return;
  }
  const requestId = nextFaviconRequestId();
  const href = await resolveFaviconHref(orgId);
  if (!isLatestFaviconRequest(requestId)) {
    return;
  }
  setDocumentFavicon(href);
};

const extractColorPair = (cfg) => {
  if (!cfg || typeof cfg !== 'object') {
    return null;
  }
  const primary = cfg.primaryColor || cfg.brandColors?.primaryColor || cfg.brandColor;
  const hover = cfg.primaryHoverColor || cfg.brandColors?.primaryHoverColor;
  if (primary || hover) {
    return {
      primaryColor: primary || null,
      primaryHoverColor: hover || primary || null
    };
  }
  return null;
};

/**
 * Resolve primary brand colors from window.__BRAND (supporting v1 and per-org).
 * @param {string} [orgId]
 * @returns {{ primaryColor: string|null, primaryHoverColor: string|null }|null}
 */
export const resolveOriginBrandColors = (orgId) => {
  const brand = getOriginBrandConfig();
  if (!brand) {
    return null;
  }
  const trimmedOrg = resolveActiveOrgId(orgId);
  const orgColors = extractColorPair(trimmedOrg && brand.orgs && brand.orgs[trimmedOrg]);
  if (orgColors) {
    return orgColors;
  }
  return extractColorPair(brand);
};

/**
 * Reset root CSS variables to default Zinier palette.
 */
export const resetBrandColors = () => {
  if (typeof document === 'undefined') {
    return;
  }
  document.documentElement.style.removeProperty('--primary-color');
  document.documentElement.style.removeProperty('--zinier-primary-color');
  document.documentElement.style.removeProperty('--primary-hover-color');
  document.documentElement.style.removeProperty('--zinier-primary-hover');
};

/**
 * Apply brand primary colors on document.documentElement (:root).
 * Falls back cleanly to CSS defaults when unset.
 * @param {string} [orgId]
 */
export const applyBrandColors = (orgId) => {
  if (typeof document === 'undefined') {
    return;
  }
  const colors = resolveOriginBrandColors(orgId);
  if (colors && colors.primaryColor) {
    document.documentElement.style.setProperty('--primary-color', colors.primaryColor);
    document.documentElement.style.setProperty('--zinier-primary-color', colors.primaryColor);
    const hover = colors.primaryHoverColor || colors.primaryColor;
    document.documentElement.style.setProperty('--primary-hover-color', hover);
    document.documentElement.style.setProperty('--zinier-primary-hover', hover);
  } else {
    resetBrandColors();
  }
};
